import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../stores/authStore";
import { APP_NAME, BRAND } from "../config";
import { useI18nNavigate } from "../hooks/useI18nNavigate";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { InternationalPhoneInput, validatePhone, isSupportedPhoneCountry } from "../components/auth/InternationalPhoneInput";
import { getOTPDeliveryMessage } from "../utils/otpMessages";
import logoHeader from "../assets/landing/logo-header.svg";

type Step = "phone" | "otp";

export function LoginPage() {
  const navigate = useI18nNavigate();
  const { requestOTP, verifyOTP, isLoading } = useAuthStore();
  const { t } = useTranslation("auth");

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [country, setCountry] = useState("");
  const [code, setCode] = useState("");
  const [deliveryChannel, setDeliveryChannel] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate phone number
    if (!validatePhone(phone)) {
      setError(t("login.error.invalidPhone", "Please enter a valid phone number"));
      return;
    }

    // The country dropdown is restricted, but a phone number can still be typed/pasted
    // for an unsupported country. Reject it client-side (UX only — the backend is the
    // source of truth and re-validates country_code independently).
    if (!isSupportedPhoneCountry(phone)) {
      setError(t("login.error.unsupportedCountry", "This country is not supported yet. Please use a United States, Jordan, or Kuwait phone number."));
      return;
    }

    try {
      // Login flow: the phone must already exist in the customers table.
      // A missing record is rejected here (before the OTP step) and surfaced
      // as an inline error under the phone field.
      const result = await requestOTP(phone, countryCode, country, true);
      setDeliveryChannel(result.delivery_channel);
      setStep("otp");
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t("login.error.notFound"));
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await verifyOTP(phone, code, countryCode, country);
      navigate("/dashboard");
    } catch (err: any) {
      setError(err?.response?.data?.error ?? t("login.error.default"));
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf8] flex items-center justify-center px-4">
      {/* Language switcher — top right */}
      <div className="fixed top-4 end-4 z-50">
        <LanguageSwitcher />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            <img src={logoHeader} alt={APP_NAME} className="h-[62px] w-auto" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          {/* Step indicators */}
          <div className="flex items-center gap-2 mb-6">
            <div
              className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                step === "phone" ? "text-white" : "text-white"
              }`}
              style={{ backgroundColor: BRAND.red }}
            >
              {step === "otp" ? <ShieldCheck className="w-3.5 h-3.5" /> : "1"}
            </div>
            <div className="flex-1 h-px bg-gray-200" />
            <div
              className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                step === "otp"
                  ? "text-white"
                  : "text-gray-400 border border-gray-200"
              }`}
              style={step === "otp" ? { backgroundColor: BRAND.red } : {}}
            >
              2
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === "phone" ? (
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 16 }}
              >
                <h1 className="text-xl font-bold text-gray-900 mb-1">
                  {t("login.phoneTitle", "Sign in")}
                </h1>
                <p className="text-sm text-gray-500 mb-6">
                  {t(
                    "login.phoneSubtitle",
                    "We'll send you a verification code",
                  )}
                </p>

                {error && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleRequestOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("login.phone", "Phone Number")}
                    </label>
                    <InternationalPhoneInput
                      value={phone}
                      onChange={setPhone}
                      onCountryChange={(code, name) => {
                        setCountryCode(code);
                        setCountry(name);
                      }}
                      placeholder={t(
                        "login.phonePlaceholder",
                        "+1 555 000 0000",
                      )}
                      autoFocus
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || !phone}
                    className="w-full disabled:bg-gray-200 text-white font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 mt-2"
                    style={{
                      backgroundColor:
                        isLoading || !phone ? undefined : BRAND.red,
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading && phone)
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.backgroundColor = BRAND.redHover;
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading && phone)
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.backgroundColor = BRAND.red;
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : null}
                    {t("login.sendCode", "Send Verification Code")}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="otp"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
              >
                <h1 className="text-xl font-bold text-gray-900 mb-1">
                  {t("login.otpTitle", "Enter verification code")}
                </h1>
                <p className="text-sm text-gray-500 mb-6">
                  {getOTPDeliveryMessage(deliveryChannel, phone)}
                </p>

                {error && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("login.otpCode", "Verification Code")}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, ""))
                      }
                      required
                      autoFocus
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 transition-colors text-center tracking-[0.4em] font-mono text-lg"
                      style={
                        { "--tw-ring-color": BRAND.red } as React.CSSProperties
                      }
                      placeholder="••••••"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || code.length < 6}
                    className="w-full disabled:bg-gray-200 text-white font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 mt-2"
                    style={{
                      backgroundColor:
                        isLoading || code.length < 6 ? undefined : BRAND.red,
                    }}
                    onMouseOver={(e) => {
                      if (!isLoading && code.length >= 6)
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.backgroundColor = BRAND.redHover;
                    }}
                    onMouseOut={(e) => {
                      if (!isLoading && code.length >= 6)
                        (
                          e.currentTarget as HTMLButtonElement
                        ).style.backgroundColor = BRAND.red;
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : null}
                    {t("login.verify", "Verify & Sign In")}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setCode("");
                    setError(null);
                  }}
                  className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {t("login.changePhone", "← Change phone number")}
                </button>

                <button
                  type="button"
                  disabled={isLoading}
                  onClick={async () => {
                    setError(null);
                    try {
                      await requestOTP(phone, countryCode, country, true);
                    } catch (err: any) {
                      setError(
                        err?.response?.data?.error ?? t("login.error.notFound"),
                      );
                    }
                  }}
                  className="w-full mt-1 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {t("login.resendCode", "Resend code")}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
